import React, {useState, useEffect} from 'react';
import NavbarRegister from '../components/NavbarRegister';
import {EyeSlashIcon, EyeIcon} from '@heroicons/react/24/outline';
import {createUserWithEmailAndPassword} from 'firebase/auth';
import {collection, getDocs, setDoc, doc, query, where} from 'firebase/firestore';
import {auth, database} from '../config/firebaseConfig';
import {useNavigate, NavLink} from 'react-router-dom';
import {motion} from 'framer-motion';
import AlertBox from '../components/AlertBox';
import { Toaster } from 'react-hot-toast';

function RegisterPage() {
    const WAIT_TIME = 2000;
    const WARNING = "warning";
    
    const [newData, setNewData] = useState(true);
    const [open, setOpen] = useState(false);
    
    const [users, setUsers] = useState(null);

    let registerIDExist = false;
    const [registerName, setRegisterName] = useState("");
    const [registerLastName, setRegisterLastName] = useState("");
    const [registerID, setRegisterID] = useState("");
    const [registerPass, setRegisterPass] = useState("");
    const [registerEmail, setRegisterEmail] = useState("");

    const [shouldRedirect, setShouldRedirect] = useState(false);

    const navigate = useNavigate();

    async function registerUser() {
        try{
            const createdUser = await createUserWithEmailAndPassword(
                auth,
                registerEmail.trim(),
                registerPass
            ).then((createdUser) => {
                return createdUser;
            });

            if (createdUser) {
                const userRef = doc(database, `users/${createdUser.user.uid}`);
                setDoc(userRef, {client_id:registerID.trim(), name:registerName.trim(), lastname:registerLastName.trim(), email: registerEmail.trim(), role:"user"})
                setShouldRedirect(true);
            }
        } catch (e) {
            console.log(e.message);
            switch(e.message) {
                case "Firebase: Error (auth/invalid-email).":
                    AlertBox(WARNING, '¡Debe ingresar un correo electrónico válido!')
                    break;
                case "Firebase: Password should be at least 6 characters (auth/weak-password).":
                    AlertBox(WARNING, '¡Debe ingresar una contraseña válida (6 carácteres de largo)!')
                    break;
                case "Firebase: Error (auth/email-already-in-use).":
                    AlertBox(WARNING, '¡El correo electrónico ya se encuentra en uso, trate de iniciar sesión!')
                    break;
                default:
                    AlertBox(WARNING, '¡Error desconocido mientras se creaba su cuenta!')
                    break;
            } 
        }
    }

    function submitHandler(e) {
        e.preventDefault();
        registerIDExist = false;
        if (registerID.length < 9 || registerID.match(/[-!$%^&*()_+|~=`{}[\]:";'<>?,./]/)){
            AlertBox(WARNING, "Por favor revisa tu cédula de identidad, no use guiones");
            return;
        }

        if (registerName.match(/[-!$%^&*()_+|~=`{}[\]:";'<>?,./]/)) {
            AlertBox(WARNING, "Por favor no use caracteres especiales");
            return;
        }

        if (registerLastName.match(/[-!$%^&*()_+|~=`{}[\]:";'<>?,./]/)) {
            AlertBox(WARNING, "Por favor no use caracteres especiales");
            return;
        }
    
        users?.filter((user) => (
            user.client_id?.includes(registerID)
        )).map(async (result) => {
            AlertBox(WARNING, `Ya existe un usuario con la cédula ${result.client_id}`);
            registerIDExist = true
            return;
        })

        if (registerIDExist) {
            return
        }
        registerUser();
    }

    useEffect(() => {
        if (shouldRedirect) {
            navigate('/');
        }
        const interval = setInterval(() => { //Autorize re-render
            if (newData) {
                const usersCollectionRef = collection(database, "users");
                const getUsers = async() => {
                    const userQuery = query(usersCollectionRef, where("role", "==", "user"));
                    const data = await getDocs(userQuery);
                    setUsers(data.docs.map((doc) => ({...doc.data(), id: doc.id})));
                }
                
                getUsers();
                setNewData(false);
                }
            }, WAIT_TIME);
        return () => clearInterval(interval);
      });

    function toggle() {
      setOpen(!open);
    }

    return (
        <div>
            <div>
                <Toaster
                toastOptions={{
                duration:3000,
            }}/> 
            </div>

            <NavbarRegister />
            
            <form onSubmit={submitHandler} className='parent-section p-0 mx-0'>
                <div className='bg-white w-full h-screen flex overflow-hidden flex-col lg:flex-row'>

                    <motion.div 
                        className='flex w-full h-1/4 lg:w-2/5 lg:h-screen bg-white bg-hero-access-alternative relative -top-1 -left-1'
                        initial={{ opacity: 1, x: "10%"}}
                        animate={{ opacity: 1, x: 0}}
                        exit={{ opacity: 1, x: "-10%" }}
                        transition={{
                            type:"spring",
                            duration: 1,
                            ease: [0, 0.71, 0.2, 1.01],
                            stiffness:100
                        }}>
                        <div className='w-full flex flex-col justify-center items-center'>
                            <div className='w-auto h-auto bg-popup-alternative flex flex-col justify-center items-center p-5 space-y-3 sm:space-y-4 rounded-xl'>
                                <h2 className="text-xl sm:text-2xl text-white font-bold form1:text-3xl">¿Tienes cuenta?</h2>
                                <p className="w-80 text-center text-sm sm:text-base font-normal text-white form1:text-xl form1:w-96">Si ya tienes una cuenta, puedes iniciar sesión para acceder a todos los beneficios.</p>
                                <NavLink to="/Login" 
                                className='flex w-28 h-8 text-sm text-center items-center justify-center sm:text-base sm:w-48 sm:h-11 bg-white text-black rounded font-bold form1:text-xl
                                hover:text-primary-100 hover:bg-slate-50'>Iniciar Sesión</NavLink>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div 
                        className='space-y-3 w-full h-3/4 flex flex-col justify-center sm:space-y-4 items-center lg:w-3/5 lg:h-screen'
                        initial={{ opacity: 1, scale: 0.8}}
                        animate={{ opacity: 1, scale: 1}}
                        transition={{
                            type:"spring",
                            duration: 1,
                            ease: [0, 0.71, 0.2, 1.01],
                            stiffness:100
                        }}>
                        <h2 className="text-xl sm:text-2xl text-primary-100 font-bold form1:text-3xl">Crear una cuenta</h2>
                        <p className="text-sm sm:text-base font-normal form1:text-xl">Para crear una cuenta rellene los siguientes datos</p>
                        <span className='w-4/5 border border-navFont-100 sm:w-2/3'></span>
                        
                        <div id="inputID" className='w-60 sm:w-80 relative shadow-sm form1:w-1/3'>
                            <input onChange={(ev) => {setRegisterID(ev.target.value)}} type={'text'} placeholder="Cédula" 
                            className='text-sm sm:text-base w-full h-input-height py-2 px-3 text-input-font bg-primary-300 
                            focus:outline-none focus:border focus:border-primary-100 focus:shadow-md' 
                            value={registerID} required/>
                        </div>

                        <div className='flex w-60 sm:w-80 relative shadow-sm form1:w-1/3 space-x-3'>
                            <input onChange={(ev) => {setRegisterName(ev.target.value)}} type={'text'} placeholder="Nombre" 
                            className='text-sm sm:text-base w-1/2 h-input-height py-2 px-3 text-input-font bg-primary-300 
                            focus:outline-none focus:border focus:border-primary-100 focus:shadow-md' 
                            value={registerName} required/>

                            <input onChange={(ev) => {setRegisterLastName(ev.target.value)}} type={'text'} placeholder="Apellido" 
                            className='text-sm sm:text-base w-1/2 h-input-height py-2 px-3 text-input-font bg-primary-300 
                            focus:outline-none focus:border focus:border-primary-100 focus:shadow-md' 
                            value={registerLastName} required/>
                        </div>

                        <div className='w-60 sm:w-80 relative shadow-sm form1:w-1/3'>
                            <input onChange={(ev) => {setRegisterEmail(ev.target.value)}} type={'text'} placeholder="Correo Electrónico" 
                            className='text-sm sm:text-base w-full h-input-height py-2 px-3 text-input-font bg-primary-300 
                            focus:outline-none focus:border focus:border-primary-100 focus:shadow-md' 
                            value={registerEmail} required/>
                        </div>
                        
                        <div className='w-60 sm:w-80 relative shadow-sm form1:w-1/3'>
                            <input onChange={(ev) => {setRegisterPass(ev.target.value)}} type={(open === false)? "password":"text"} placeholder="Contraseña" 
                            className='w-full h-input-height py-2 pr-9 pl-3 text-input-font bg-primary-300 focus:outline-none 
                            focus:border focus:border-primary-100 focus:shadow-md text-sm sm:text-base' 
                            value={registerPass} required/>

                            <div className='absolute inset-y-0 right-0 flex items-center hover:cursor-pointer'>
                                {
                                    (open === false)? <EyeSlashIcon onClick={toggle} className='text-input-font h-4 w-4 sm:h-6 sm:w-6 mx-2' />:
                                    <EyeIcon onClick={toggle} className='text-primary-100 h-4 w-4 sm:h-6 sm:w-6 mx-2' />
                                }
                            </div>
                        </div>
                        
                        <p className='text-sm sm:text-base form1:text-xl'>
                            <input type={"checkbox"} 
                            className="mr-2 border-primary-100" required/>He leído los <a href="https://firebase.google.com/support/privacy?hl=es-419" className='text-primary-100'>términos y condiciones</a></p>
                        
                        <button type={'submit'} className='w-28 h-8 text-sm sm:text-base sm:w-48 sm:h-11 bg-primary-100 text-white rounded font-bold form1:text-xl hover:bg-primary-200'>Registrarme</button>

                    </motion.div>
                </div>
            </form>
        </div>
    )
}

export default RegisterPage
